import React, { useMemo, useState } from 'react';
import { Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import './EquityCurve.css';

const EquityCurve = ({ entries = [], height = 300 }) => {
  const [timeRange, setTimeRange] = useState('all');

  // Sort entries by date and calculate cumulative P/L
  const chartData = useMemo(() => {
    if (!entries.length) return [];

    // Filter by time range
    const now = new Date();
    let filteredEntries = [...entries];

    if (timeRange === '7d') {
      const cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filteredEntries = entries.filter(e => new Date(e.trade_date) >= cutoff);
    } else if (timeRange === '30d') {
      const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filteredEntries = entries.filter(e => new Date(e.trade_date) >= cutoff);
    } else if (timeRange === '90d') {
      const cutoff = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      filteredEntries = entries.filter(e => new Date(e.trade_date) >= cutoff);
    }

    // Sort by date
    const sorted = filteredEntries.sort((a, b) =>
      new Date(a.trade_date) - new Date(b.trade_date)
    );

    // Calculate cumulative P/L
    let cumulative = 0;
    return sorted.map((entry, index) => {
      cumulative += entry.profit_loss || 0;
      return {
        date: new Date(entry.trade_date),
        pl: entry.profit_loss || 0,
        cumulative,
        instrument: entry.instrument,
        index
      };
    });
  }, [entries, timeRange]);

  // Calculate chart dimensions and scales
  const chartMetrics = useMemo(() => {
    if (!chartData.length) return null;

    const values = chartData.map(d => d.cumulative);
    const maxVal = Math.max(...values, 0);
    const minVal = Math.min(...values, 0);
    const range = maxVal - minVal || 1;

    // Add padding
    const padding = range * 0.1;
    const adjustedMax = maxVal + padding;
    const adjustedMin = minVal - padding;
    const adjustedRange = adjustedMax - adjustedMin;

    // Find max drawdown
    let peak = 0;
    let maxDrawdown = 0;
    let currentDrawdown = 0;
    chartData.forEach(d => {
      if (d.cumulative > peak) peak = d.cumulative;
      currentDrawdown = peak - d.cumulative;
      if (currentDrawdown > maxDrawdown) maxDrawdown = currentDrawdown;
    });

    return {
      maxVal: adjustedMax,
      minVal: adjustedMin,
      range: adjustedRange,
      maxDrawdown,
      finalPL: chartData[chartData.length - 1]?.cumulative || 0,
      totalTrades: chartData.length
    };
  }, [chartData]);

  const [hoveredPoint, setHoveredPoint] = useState(null);

  // SVG dimensions
  const svgWidth = 800;
  const svgHeight = height - 80;
  const padding = { top: 20, right: 20, bottom: 30, left: 60 };
  const chartWidth = svgWidth - padding.left - padding.right;
  const chartHeight = svgHeight - padding.top - padding.bottom;

  // Generate path
  const generatePath = () => {
    if (!chartData.length || !chartMetrics) return '';

    const points = chartData.map((d, i) => {
      const x = padding.left + (i / (chartData.length - 1 || 1)) * chartWidth;
      const y = padding.top + chartHeight - ((d.cumulative - chartMetrics.minVal) / chartMetrics.range) * chartHeight;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    });

    return points.join(' ');
  };

  // Generate area fill path
  const generateAreaPath = () => {
    if (!chartData.length || !chartMetrics) return '';

    const linePath = generatePath();
    const zeroY = padding.top + chartHeight - ((0 - chartMetrics.minVal) / chartMetrics.range) * chartHeight;
    const startX = padding.left;
    const endX = padding.left + chartWidth;

    return `${linePath} L ${endX} ${zeroY} L ${startX} ${zeroY} Z`;
  };

  // Generate grid lines
  const generateGridLines = () => {
    if (!chartMetrics) return [];

    const lines = [];
    const numLines = 5;

    for (let i = 0; i <= numLines; i++) {
      const value = chartMetrics.minVal + (chartMetrics.range * i / numLines);
      const y = padding.top + chartHeight - (i / numLines) * chartHeight;

      lines.push({
        y,
        value,
        label: `$${value >= 0 ? '+' : ''}${value.toFixed(0)}`
      });
    }

    return lines;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!chartData.length) {
    return (
      <div className="equity-curve empty">
        <Typography variant="body2" color="textSecondary">
          No trade data available for equity curve
        </Typography>
      </div>
    );
  }

  const isPositive = chartMetrics?.finalPL >= 0;

  return (
    <div className="equity-curve">
      {/* Header */}
      <div className="equity-header">
        <div className="equity-title">
          <Typography variant="h6">Equity Curve</Typography>
          <div className={`equity-total ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
            <span>${Math.abs(chartMetrics?.finalPL || 0).toFixed(2)}</span>
          </div>
        </div>

        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(e, val) => val && setTimeRange(val)}
          size="small"
          className="time-range-toggle"
        >
          <ToggleButton value="7d">7D</ToggleButton>
          <ToggleButton value="30d">30D</ToggleButton>
          <ToggleButton value="90d">90D</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Stats Row */}
      <div className="equity-stats">
        <div className="stat-item">
          <span className="stat-label">Total Trades</span>
          <span className="stat-value">{chartMetrics?.totalTrades || 0}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Max Drawdown</span>
          <span className="stat-value negative">-${(chartMetrics?.maxDrawdown || 0).toFixed(2)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Peak</span>
          <span className="stat-value positive">+${(chartMetrics?.maxVal || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Chart */}
      <div className="equity-chart-container">
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="xMidYMid meet"
          className="equity-svg"
        >
          {/* Grid lines */}
          {generateGridLines().map((line, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={line.y}
                x2={svgWidth - padding.right}
                y2={line.y}
                className="grid-line"
              />
              <text
                x={padding.left - 8}
                y={line.y + 4}
                className="grid-label"
                textAnchor="end"
              >
                {line.label}
              </text>
            </g>
          ))}

          {/* Zero line */}
          {chartMetrics && chartMetrics.minVal < 0 && chartMetrics.maxVal > 0 && (
            <line
              x1={padding.left}
              y1={padding.top + chartHeight - ((0 - chartMetrics.minVal) / chartMetrics.range) * chartHeight}
              x2={svgWidth - padding.right}
              y2={padding.top + chartHeight - ((0 - chartMetrics.minVal) / chartMetrics.range) * chartHeight}
              className="zero-line"
            />
          )}

          {/* Area fill */}
          <path
            d={generateAreaPath()}
            className={`equity-area ${isPositive ? 'positive' : 'negative'}`}
          />

          {/* Line */}
          <path
            d={generatePath()}
            className={`equity-line ${isPositive ? 'positive' : 'negative'}`}
          />

          {/* Data points */}
          {chartData.map((d, i) => {
            const x = padding.left + (i / (chartData.length - 1 || 1)) * chartWidth;
            const y = padding.top + chartHeight - ((d.cumulative - chartMetrics.minVal) / chartMetrics.range) * chartHeight;

            return (
              <g key={i}>
                <circle
                  cx={x}
                  cy={y}
                  r={hoveredPoint === i ? 6 : 4}
                  className={`data-point ${d.pl >= 0 ? 'positive' : 'negative'} ${hoveredPoint === i ? 'hovered' : ''}`}
                  onMouseEnter={() => setHoveredPoint(i)}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              </g>
            );
          })}

          {/* X-axis labels */}
          {chartData.length > 0 && (
            <>
              <text
                x={padding.left}
                y={svgHeight - 5}
                className="axis-label"
                textAnchor="start"
              >
                {formatDate(chartData[0].date)}
              </text>
              <text
                x={svgWidth - padding.right}
                y={svgHeight - 5}
                className="axis-label"
                textAnchor="end"
              >
                {formatDate(chartData[chartData.length - 1].date)}
              </text>
            </>
          )}
        </svg>

        {/* Tooltip */}
        {hoveredPoint !== null && chartData[hoveredPoint] && (
          <div
            className="chart-tooltip"
            style={{
              left: `${((hoveredPoint / (chartData.length - 1 || 1)) * 100)}%`
            }}
          >
            <div className="tooltip-date">{formatDate(chartData[hoveredPoint].date)}</div>
            <div className="tooltip-instrument">{chartData[hoveredPoint].instrument}</div>
            <div className={`tooltip-pl ${chartData[hoveredPoint].pl >= 0 ? 'positive' : 'negative'}`}>
              Trade: {chartData[hoveredPoint].pl >= 0 ? '+' : ''}${chartData[hoveredPoint].pl.toFixed(2)}
            </div>
            <div className={`tooltip-cumulative ${chartData[hoveredPoint].cumulative >= 0 ? 'positive' : 'negative'}`}>
              Total: {chartData[hoveredPoint].cumulative >= 0 ? '+' : ''}${chartData[hoveredPoint].cumulative.toFixed(2)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EquityCurve;
